import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { formatColorName } from '@util/common.js';
import { CHARITY } from '@util/const.js';
import { deepSignal } from 'deepsignal';
import {
  SubathonTimerOverlay,
  SubathonTimerOverlayAlignments,
  SubathonTimerOverlayTextColors,
  SubathonTimerOverlayGlowEffectIntensities,
} from '@playlive/overlay-core/overlays/subathon-timer';
import useCommon from '@hooks/useCommon.js';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import Description from '@components/overlays/shared/description.jsx';
import ColorPicker from '@components/pickers/color-picker/color.picker.jsx';
import Picker from '@components/input/picker.input.jsx';
import LinkSetup from '@components/overlays/shared/link-setup.jsx';
import { Color } from '@playlive/overlay-core/colors';
import { generateRandomEffect } from './subathon-timer.helpers.js';
import { SubathonTimerDonations } from './subathon-timer.data.js';
import { useSubathonTimerSettings } from './subathon-timer.hooks.js';
import SubathonTimerSettings from './subathon-timer.settings.jsx';
import YesNoButtonInput from '@components/input/yes-no-button.input.jsx';
import YesNoButtonInputWrapper from '@components/overlays/wrapper/yes-no-button-input-wrapper.jsx';
import toast from 'react-hot-toast';
import { CheckCircle } from 'flowbite-react-icons/solid';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  textColor: Color.DEFAULT_RED,
  alignment: SubathonTimerOverlayAlignments[0],
  intensity: 'NORMAL',
  glowEffectMatches: 'yes',
  glowEffect: 'yes',
});

const timerEnd = new Date();
timerEnd.setMinutes(timerEnd.getMinutes() + 20);

/**
 * @type {import('@playlive/overlay-core').SubathonTimer}
 */
const initialSubathonTimer = {
  campaignID: '1',
  highestDonationValue: 100,
  id: '1',
  timerEnd,
  timerStart: new Date(),
  status: 'ACTIVE',
};

const initialTimerDonations = SubathonTimerDonations(timerEnd);

export default function SubathonTimerPage() {
  const { overlayInfo, overlayURL, setConfigState, tiltifyConfig } = useCommon({
    config,
  });
  const [currentTimer, setCurrentTimer] = useState(initialSubathonTimer);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [donations, setDonations] = useState([initialTimerDonations[0]]);
  const {
    settings,
    updateSettings,
    deleteSettings,
    loading: isLoadingSettings,
  } = useSubathonTimerSettings(tiltifyConfig.tiltifyCampaignID);

  const didSave = useCallback((reset = false) => {
    toast(
      <>
        <CheckCircle />
        <span>Subathon timer settings {reset ? 'reset' : 'updated'}</span>
      </>,
      {
        position: 'top-center',
        className: 'alert-success text-white',
      }
    );
  }, []);

  const applyRandomEffect = useCallback((timer) => {
    const randomEffect = generateRandomEffect(
      timer.timerEnd,
      initialTimerDonations
    );

    setDonations(() => [randomEffect]);
    setCurrentTimer((timer) => {
      if (timer !== null) {
        const timerEnd = new Date(timer.timerEnd);
        timerEnd.setTime(
          timerEnd.getTime() + randomEffect.timer_effect.timeDifference
        );

        return {
          ...timer,
          timerEnd,
        };
      }

      return null;
    });
  }, []);

  const filterColors = useCallback((color) => {
    return SubathonTimerOverlayTextColors.find((c) => c === color);
  }, []);

  const onSaveHandler = useCallback(
    (settings) => {
      setButtonsDisabled(true);
      updateSettings(settings, () => {
        didSave();
        setButtonsDisabled(false);
      });
    },
    [updateSettings, didSave]
  );

  const onResetHandler = useCallback(() => {
    setButtonsDisabled(true);
    deleteSettings(() => {
      setButtonsDisabled(false);
      didSave(true);
    });
  }, [deleteSettings, didSave]);

  useEffect(() => {
    const interval = setInterval(() => {
      applyRandomEffect(currentTimer);
    }, 6000);

    return () => {
      clearInterval(interval);
    };
  }, [applyRandomEffect, currentTimer]);

  const alignmentOptions = [
    {
      name: 'Left',
      value: SubathonTimerOverlayAlignments[0],
    },
    {
      name: 'Right',
      value: SubathonTimerOverlayAlignments[1],
    },
  ];

  const intensityOptions = [
    {
      name: 'High',
      value: SubathonTimerOverlayGlowEffectIntensities[0],
    },
    {
      name: 'Normal',
      value: SubathonTimerOverlayGlowEffectIntensities[1],
    },
    {
      name: 'Low',
      value: SubathonTimerOverlayGlowEffectIntensities[2],
    },
  ];

  const disableSettings = useMemo(() => {
    return (
      tiltifyConfig.tiltifyCampaignID === '' ||
      tiltifyConfig.tiltifyCampaignID === null
    );
  }, [tiltifyConfig.tiltifyCampaignID]);

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay overflow-x-hidden w-full min-h-72">
          <ScaledOverlayWrapper
            allowScaling={false}
            defaultScale={'scale-100'}
            innerClassName="h-[420px]"
          >
            <SubathonTimerOverlay
              glowEffectIntensity={config.intensity}
              glowEffectEnabled={config.glowEffect === 'yes'}
              glowEffectMatchesTextColor={config.glowEffectMatches === 'yes'}
              timer={currentTimer}
              donations={donations}
              textColor={config.textColor}
              alignment={config.alignment}
              donationDelay={3500}
              timeoutLength={600}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper>
          <CustomizeSection title="Choose Colors">
            <span className="label-text mb-2">
              Text color: <b>{formatColorName(config.textColor)}</b>
            </span>

            <ColorPicker
              fieldName="textColor"
              currentColor={config.textColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />
          </CustomizeSection>
          <CustomizeSection title="Alignment">
            <div className="mt-5">
              <Picker
                items={alignmentOptions}
                fieldName="alignment"
                onChange={setConfigState}
                currentValue={config.alignment}
                labelText=""
              />
            </div>
          </CustomizeSection>
          <CustomizeSection title="Text Appearance">
            <YesNoButtonInputWrapper>
              <YesNoButtonInput
                onChange={setConfigState}
                mode="checkbox"
                currentValue={config.glowEffect}
                name="glowEffect"
                label="Glow Enabled"
                className="md:mr-4 mb-2 md:mb-0 flex-1"
              />

              <YesNoButtonInput
                className="flex-1 min-w-[300px]"
                onChange={setConfigState}
                mode="checkbox"
                currentValue={config.glowEffectMatches}
                name="glowEffectMatches"
                label="Glow Matches Text Color"
              />
            </YesNoButtonInputWrapper>

            <div className="mt-1">
              <Picker
                disabled={config.glowEffect === 'no'}
                items={intensityOptions}
                fieldName="intensity"
                onChange={setConfigState}
                currentValue={config.intensity}
                labelText="Text Glow Intensity"
              />
            </div>
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <Description>
        <p className="pb-4">
          The Subathon Timer is a countdown clock used during a live streaming
          event. The timer starts with a set duration and increases whenever
          viewers donate to St. Jude. The idea is to extend the stream's length
          based on viewer contributions, creating an interactive and exciting
          experience. As viewers donate, the timer adds a random amount of time,
          potentially turning a regular stream into a marathon session. For
          added excitement, there's a small chance that a donation could remove
          time from the timer. It's a fun way to boost engagement and support
          for St. Jude!
        </p>
      </Description>

      <LinkSetup
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        campaignStepContent={
          <div className="mt-2">
            <SubathonTimerSettings
              disabled={disableSettings}
              onSave={onSaveHandler}
              onReset={onResetHandler}
              isLoading={isLoadingSettings}
              buttonsDisabled={buttonsDisabled}
              settings={settings}
            />
          </div>
        }
      />
    </div>
  );
}
